import React from "react";
import { ShieldCheck, Mail } from "lucide-react";

/**
 * R3-Phase-1 (2026-05-26) — Deprecation page for legacy public share
 * links.  The whole anonymous public-share system has been retired.
 *
 * This page renders for ANY `/share/:token` URL.  We deliberately do
 * NOT call the backend (no token validation, no API request) — the
 * backend already responds 410 Gone for `/api/public/demo/{token}`,
 * and there's nothing for an anonymous visitor to see.
 *
 * Phase 2 will introduce invitation-only delivery via magic links sent
 * over email; that flow uses a different URL shape (`/v/:invitation`).
 */
export default function PublicSharePage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <img
            src="/eyvo-logo.jpg"
            alt="Eyvo"
            className="h-10 w-10 object-contain"
            data-testid="public-brand-logo"
          />
          <span className="text-base font-semibold text-slate-900">
            Eyvo Demo Agent
          </span>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-20" data-testid="share-deprecation-page">
        <div className="rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
            <ShieldCheck size={14} className="text-indigo-600" />
            Updated link policy
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            This share link has been retired.
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-600">
            For IP protection, Eyvo has moved off anonymous public share
            links.  Every demo video is now delivered by personal email
            invitation — only the intended recipient can open it, and
            each session is audited.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Please ask the demo owner to send you a fresh invitation. If
            you're not sure who that is, reply to the person who first
            sent you this link and ask them to re-share.
          </p>

          <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Mail size={14} className="text-slate-500" />
              What changes for you
            </div>
            <ul className="space-y-1.5 text-[13px] text-slate-600">
              <li className="flex items-start gap-2">
                <span className="mt-1 inline-block h-1 w-1 flex-none rounded-full bg-slate-400" />
                Invitations arrive at your email address — no need to keep
                track of a URL.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 inline-block h-1 w-1 flex-none rounded-full bg-slate-400" />
                Each invitation is single-recipient and time-limited.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 inline-block h-1 w-1 flex-none rounded-full bg-slate-400" />
                The demo owner can revoke or extend access at any time.
              </li>
            </ul>
          </div>

          <p className="mt-8 text-xs text-slate-400" data-testid="share-deprecation-version">
            Eyvo Demo Agent · R3-Phase-1 · 2026-05-26
          </p>
        </div>
      </main>
    </div>
  );
}
